/**
 * Servicio único de conectividad (online/offline) para la capa offline.
 *
 * `navigator.onLine` da falsos positivos (puede reportar "online" con una red
 * que no llega a SharePoint), así que combinamos dos señales:
 *   - `navigator.onLine` para descartar el caso obvio de sin-red.
 *   - El resultado REAL de la última llamada a SP (`marcarSpOk`/`marcarSpFalla`),
 *     que es la verdad sobre si el backend es alcanzable.
 */

type Listener = (online: boolean) => void;

let ultimoSpOk = true; // optimista al arranque; se corrige al primer fetch
const listeners = new Set<Listener>();

/** ¿La app puede hablar con SharePoint ahora? */
export function estaOnline(): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  return ultimoSpOk;
}

/** Marca que una llamada a SP tuvo éxito (hay red real). */
export function marcarSpOk(): void {
  if (!ultimoSpOk) {
    ultimoSpOk = true;
    emit();
  }
}

/** Marca que una llamada a SP falló por red (no por permisos/404). */
export function marcarSpFalla(): void {
  if (ultimoSpOk) {
    ultimoSpOk = false;
    emit();
  }
}

/** Suscribe a cambios de conectividad. Devuelve función para desuscribir. */
export function onConnectivityChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function emit(): void {
  const v = estaOnline();
  for (const fn of listeners) fn(v);
}

/** Heurística: ¿el error proviene de un fallo de RED (no de negocio/permiso)? */
export function esErrorDeRed(e: unknown): boolean {
  if (e instanceof TypeError) return true; // fetch lanza TypeError sin red
  const msg = e instanceof Error ? e.message.toLowerCase() : "";
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("network request failed") ||
    msg.includes("load failed")
  );
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    ultimoSpOk = true;
    emit();
  });
  window.addEventListener("offline", () => {
    ultimoSpOk = false;
    emit();
  });
}
